import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  X,
  FileText,
  Printer,
  RotateCcw,
  Trash2,
  BookmarkCheck,
  Loader2,
  Sparkles,
  User,
  Phone,
  IndianRupee,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { formatINR } from "../../../Utils/helper";
import { showSuccessToast } from "../../atoms/Utlis/Toast";

// ── Default 8-Point Studio Terms Text ─────────────────────────────────────────
export const DEFAULT_STUDIO_TERMS_TEXT = `1. Payment Terms
50% Advance (Non-Refundable) at the time of booking. 40% payable immediately after completion of the event. Remaining 10% to be cleared within 2 months from the date of the event.

2. Deliverables Schedule
Raw data within 3–5 working days. Selected photos edited within 15–20 days. Final album delivered within 30 days after customer approval.

3. Client Review & Approval
Customer must select photos for album within 30 days of receiving the preview link to avoid production delays.

4. Travel & Accommodation
Travel, food, and stay arrangements for outstation events (beyond 30 km from studio) to be borne/provided by the client.

5. Cancellation & Rescheduling
Advance is non-refundable upon cancellation. Rescheduling subject to date availability with minimum 15 days prior notice.

6. Copyright & Usage
The studio retains copyright of all images/video and reserves the right to use them for promotional purposes unless agreed otherwise in writing.

7. Force Majeure
The studio is not liable for failure to perform duties due to acts of God, strikes, equipment failure beyond control, or other unforeseen events.

8. Equipment & Safety
The client is responsible for ensuring safe working conditions for the crew and equipment at all event locations.`;

const STORAGE_DEFAULT_TERMS_KEY = "studio_custom_terms_default";

export const getSavedDefaultTerms = (): string => {
  try {
    const saved = localStorage.getItem(STORAGE_DEFAULT_TERMS_KEY);
    if (saved && saved.trim()) return saved;
  } catch {}
  return DEFAULT_STUDIO_TERMS_TEXT;
};

export const saveAsStudioDefaultTerms = (text: string): void => {
  try {
    localStorage.setItem(STORAGE_DEFAULT_TERMS_KEY, text);
  } catch {}
};

// ── Section 5.2 Document Terms Component ─────────────────────────────────────
export interface TermsSectionProps {
  addTerms: boolean;
  terms: string | null;
}

export const DocumentTermsSection: React.FC<TermsSectionProps> = ({ addTerms, terms }) => {
  if (!addTerms || !terms || !terms.trim()) return null;
  return (
    <div className="mt-8 pt-4 border-t border-gray-200" style={{ pageBreakInside: "avoid" }}>
      <div className="flex items-center gap-2 mb-3">
        {/* Accent Bar */}
        <div className="w-1 h-4 bg-gray-900 rounded-full" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-900">
          TERMS & CONDITIONS
        </h3>
      </div>
      <div className="text-[11px] leading-relaxed text-gray-700 whitespace-pre-wrap font-sans">
        {terms}
      </div>
    </div>
  );
};

// ── Modal Component ──────────────────────────────────────────────────────────

export interface DocumentTermsPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: "quotation" | "order" | "invoice";
  documentNumber: string;
  customerName?: string;
  customerPhone?: string;
  totalAmount?: string | number;
  initialAddTerms?: boolean;
  initialTerms?: string | null;
  onDownloadPdf: (addTerms: boolean, terms: string) => Promise<void>;
  loading?: boolean;
}

export const DocumentTermsPdfModal: React.FC<DocumentTermsPdfModalProps> = ({
  isOpen,
  onClose,
  documentType,
  documentNumber,
  customerName,
  customerPhone,
  totalAmount,
  initialAddTerms = true,
  initialTerms,
  onDownloadPdf,
  loading = false,
}) => {
  const [addTerms, setAddTerms] = useState<boolean>(initialAddTerms);
  const [terms, setTerms] = useState<string>(() => {
    if (initialTerms && initialTerms.trim()) return initialTerms;
    return getSavedDefaultTerms();
  });

  useEffect(() => {
    if (isOpen) {
      setAddTerms(initialAddTerms);
      if (initialTerms && initialTerms.trim()) {
        setTerms(initialTerms);
      } else {
        setTerms(getSavedDefaultTerms());
      }
    }
  }, [isOpen, initialAddTerms, initialTerms]);

  if (!isOpen) return null;

  const handleResetToStandard = () => {
    setTerms(DEFAULT_STUDIO_TERMS_TEXT);
  };

  const handleSaveAsDefault = () => {
    saveAsStudioDefaultTerms(terms);
    showSuccessToast("Terms saved as studio default!");
  };

  const handleClear = () => {
    setTerms("");
  };

  const handleSubmit = async () => {
    await onDownloadPdf(addTerms, terms);
  };

  return createPortal(
    <ModalOverlay onClick={() => !loading && onClose()}>
      <ModalShell
        style={{ maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <FileText size={11} />
                PDF Terms & Conditions
              </HeroBadge>
              <ModalTitle>Download {documentType === "quotation" ? "Quotation" : "Order"} PDF</ModalTitle>
              <ModalSubtitle>
                Customize terms & conditions for {documentNumber}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn
              type="button"
              onClick={() => !loading && onClose()}
              disabled={loading}
              aria-label="Close"
            >
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody style={{ padding: "20px 24px", overflowY: "auto", maxHeight: "calc(90vh - 160px)" }}>
          {/* Document Summary Card */}
          <SummaryCard>
            {customerName && (
              <SummaryItem>
                <User size={13} color="#6b7280" />
                <span className="label">Customer:</span>
                <span className="value">{customerName}</span>
              </SummaryItem>
            )}
            {customerPhone && (
              <SummaryItem>
                <Phone size={13} color="#6b7280" />
                <span className="label">Phone:</span>
                <span className="value">{customerPhone}</span>
              </SummaryItem>
            )}
            {totalAmount !== undefined && totalAmount !== null && (
              <SummaryItem>
                <IndianRupee size={13} color="#6b7280" />
                <span className="label">Total:</span>
                <span className="value font-bold text-gray-900">
                  ₹{formatINR(totalAmount)}
                </span>
              </SummaryItem>
            )}
          </SummaryCard>

          {/* Toggle Add Terms Checkbox */}
          <CheckboxContainer>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addTerms}
                onChange={(e) => setAddTerms(e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">
                Include Terms & Conditions in generated PDF
              </span>
            </label>
            <span className="status-pill">
              {addTerms ? "Terms Enabled (2 Pages)" : "Terms Disabled"}
            </span>
          </CheckboxContainer>

          {/* Point-by-point Terms Textarea */}
          {addTerms && (
            <div style={{ marginTop: "14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Studio Terms & Conditions (Point by Point)
                </label>
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  Each line break is preserved in the PDF
                </span>
              </div>
              <textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="Enter terms and conditions (point by point)..."
                rows={9}
                className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-1 focus:ring-blue-500 font-sans leading-relaxed"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  padding: "10px 12px",
                  fontSize: "12px",
                  lineHeight: "1.6",
                  color: "#111827",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />

              {/* Quick Actions Row */}
              <QuickActionsRow>
                <QuickBtn type="button" onClick={handleResetToStandard} title="Reset to standard 8 photography points">
                  <RotateCcw size={12} />
                  <span>Standard 8 Points</span>
                </QuickBtn>
                <QuickBtn type="button" onClick={handleSaveAsDefault} title="Save current text as studio default for all future PDFs">
                  <BookmarkCheck size={12} color="#059669" />
                  <span style={{ color: "#059669", fontWeight: 600 }}>Save as Studio Default</span>
                </QuickBtn>
                <QuickBtn type="button" onClick={handleClear} title="Clear all text">
                  <Trash2 size={12} color="#dc2626" />
                  <span style={{ color: "#dc2626" }}>Clear</span>
                </QuickBtn>
              </QuickActionsRow>
            </div>
          )}

          {/* Action Buttons */}
          <ModalFooter>
            <CancelBtn
              type="button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </CancelBtn>
            <PrimaryBtn
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 22px",
                borderRadius: "10px",
                minWidth: 150,
                justifyContent: "center",
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spin" />
                  <span>Generating PDF...</span>
                </>
              ) : (
                <>
                  <Printer size={16} />
                  <span>Download PDF</span>
                </>
              )}
            </PrimaryBtn>
          </ModalFooter>
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

// ── Styled Helpers ───────────────────────────────────────────────────────────

const SummaryCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 14px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;

  .label {
    color: #6b7280;
    font-weight: 500;
  }
  .value {
    color: #111827;
    font-weight: 600;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
  }

  .checkbox-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #111827;
  }

  .checkbox-text {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
  }

  .status-pill {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    background: #e2e8f0;
    padding: 3px 8px;
    border-radius: 999px;
  }
`;

const QuickActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const QuickBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
`;

const CancelBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default DocumentTermsPdfModal;
